import React, { createContext, useState, useContext, useEffect } from 'react';
import { useData } from './DataContext';

export type Song = {
  id: string;
  title: string;
  artist: string;
  coverArt: string;
  duration: number;
  genre: string;
  language: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Expert';
  popularity: number;
  releaseYear: number;
  hasGuideVocals: boolean;
};

type SongContextType = {
  songs: Song[];
  currentSong: Song | null;
  setCurrentSong: (song: Song | null) => void;
  favorites: Set<string>;
  toggleFavorite: (songId: string) => void;
  recentSongs: Song[];
};

// Mock data
const mockSongs: Song[] = [
  {
    id: 'song1',
    title: 'Shape of You',
    artist: 'Ed Sheeran',
    coverArt: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    duration: 235,
    genre: 'Pop',
    language: 'English',
    difficulty: 'Medium',
    popularity: 94,
    releaseYear: 2017,
    hasGuideVocals: true,
  },
  {
    id: 'song2',
    title: 'Bohemian Rhapsody',
    artist: 'Queen',
    coverArt: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    duration: 367,
    genre: 'Rock',
    language: 'English',
    difficulty: 'Hard',
    popularity: 92,
    releaseYear: 1975,
    hasGuideVocals: true,
  },
  {
    id: 'song3',
    title: 'Billie Jean',
    artist: 'Michael Jackson',
    coverArt: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    duration: 294,
    genre: 'Pop',
    language: 'English',
    difficulty: 'Medium',
    popularity: 89,
    releaseYear: 1982,
    hasGuideVocals: true,
  },
  {
    id: 'song4',
    title: 'Shallow',
    artist: 'Lady Gaga & Bradley Cooper',
    coverArt: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    duration: 217,
    genre: 'Pop',
    language: 'English',
    difficulty: 'Medium',
    popularity: 88,
    releaseYear: 2018,
    hasGuideVocals: true,
  },
  {
    id: 'song5',
    title: 'Despacito',
    artist: 'Luis Fonsi ft. Daddy Yankee',
    coverArt: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    duration: 229,
    genre: 'Latin Pop',
    language: 'Spanish',
    difficulty: 'Medium',
    popularity: 95,
    releaseYear: 2017,
    hasGuideVocals: true,
  },
  {
    id: 'song6',
    title: 'Hello',
    artist: 'Adele',
    coverArt: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    duration: 295,
    genre: 'Pop',
    language: 'English',
    difficulty: 'Medium',
    popularity: 87,
    releaseYear: 2015,
    hasGuideVocals: true,
  },
];

const SongContext = createContext<SongContextType | undefined>(undefined);

export const useSongs = () => {
  const context = useContext(SongContext);
  if (!context) {
    throw new Error('useSongs must be used within a SongProvider');
  }
  return context;
};

export const SongProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { songService } = useData();
  const [songs, setSongs] = useState<Song[]>([]);
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set(['song1', 'song3']));
  const [recentSongs, setRecentSongs] = useState<Song[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    // Only load songs if they haven't been loaded yet
    // This prevents loading on every page that doesn't need songs
    if (hasLoadedOnce) return;

    const loadSongs = async () => {
      setIsLoading(true);
      try {
        const allSongs = await songService.getAllSongs();
        setSongs(allSongs);
        setRecentSongs(allSongs.slice(0, 2));
        setHasLoadedOnce(true);
      } catch (error) {
        console.error('Error loading songs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only load if songs are actually needed (when the context is accessed)
    // For now, we'll delay loading to avoid blocking the app startup
    const timer = setTimeout(() => {
      loadSongs();
    }, 100);

    return () => clearTimeout(timer);
  }, [songService, hasLoadedOnce]);

  const toggleFavorite = (songId: string) => {
    setFavorites((prevFavorites) => {
      const newFavorites = new Set(prevFavorites);
      if (newFavorites.has(songId)) {
        newFavorites.delete(songId);
      } else {
        newFavorites.add(songId);
      }
      return newFavorites;
    });
  };

  return (
    <SongContext.Provider
      value={{
        songs,
        currentSong,
        setCurrentSong,
        favorites,
        toggleFavorite,
        recentSongs,
      }}
    >
      {children}
    </SongContext.Provider>
  );
};